import React, { useEffect, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { PlusIcon } from '@heroicons/react/24/solid';
import { AnimatedError, STRIP_ICON_BUTTON_CLASS, STRIP_ICON_CLASS } from '@charitystack/shared';
import AnimatedAccordionReveal from '../Components/AnimatedAccordionReveal';
import {
  calculateScheduledDates,
  createScheduleConfigKey,
  getScheduleConfigForFrequency,
  hasScheduledSelection,
  refitScheduledEndDate,
  resolveFixedChargeDayStartDate,
} from './scheduleConfigUtils';
import { loadScheduledPicker } from './loadScheduledPicker.js';

// A failed prewarm must not become an unhandled rejection. The active picker
// still makes its request through lazyWithRetry, which owns chunk recovery.
const prewarmPicker = () => { loadScheduledPicker().catch(() => {}); };

const formatDateValue = (value) => {
  if (!value) return null;

  if (value && typeof value === 'object' && value.format) {
    return value.format('MMM D, YYYY');
  }

  if (value instanceof Date) {
    return dayjs(value).format('MMM D, YYYY');
  }

  if (typeof value === 'string') {
    return dayjs(value).format('MMM D, YYYY');
  }

  return null;
};

const formatTimeValue = (value) => {
  if (!value) return null;
  if (value === 'now') return 'Now';

  const [hourText, minuteText] = value.split(':');
  const hour = parseInt(hourText, 10);
  const minute = parseInt(minuteText || '0', 10);

  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return value;
  }

  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

  return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
};

// The summary reads dates the way a sentence does: "Oct 25, 2026".
const formatDateValueLong = formatDateValue;

// Pure ordinal suffix for a day number (1 → "1st", 22 → "22nd", 31 → "31st").
// Callers that need a "last day" label (e.g. monthly day 31) handle that case
// themselves; this helper stays domain-agnostic.
const ordinal = (day) => {
  const j = day % 10;
  const k = day % 100;
  if (j === 1 && k !== 11) return `${day}st`;
  if (j === 2 && k !== 12) return `${day}nd`;
  if (j === 3 && k !== 13) return `${day}rd`;
  return `${day}th`;
};

const WEEKDAY_LABELS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAY_LABELS_LONG = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];
const MONTH_LABELS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];
const MONTH_LABELS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Compact natural-language form of a fixed recurring charge day, tuned for the
// small summary chip. All variants lead with the recurrence word for a uniform
// pattern: "Every Wed" (weekly) / "Monthly on the 31st" or
// "Monthly on the last day" (monthly) / "Annually on Jan 3" (annually).
// Null when there's no fixed charge day.
const describeFixedChargeDayShort = (fixedChargeDay, selectedFrequency) => {
  if (fixedChargeDay === null || fixedChargeDay === undefined || fixedChargeDay === '') return null;

  if (selectedFrequency === 'WEEKLY') {
    const idx = parseInt(fixedChargeDay, 10);
    if (Number.isNaN(idx) || idx < 0 || idx > 6) return null;
    return `Every ${WEEKDAY_LABELS_SHORT[idx]}`;
  }

  if (selectedFrequency === 'MONTHLY') {
    const day = parseInt(fixedChargeDay, 10);
    if (Number.isNaN(day) || day < 1 || day > 31) return null;
    return day === 31 ? 'Monthly on the last day' : `Monthly on the ${ordinal(day)}`;
  }

  if (selectedFrequency === 'ANNUALLY') {
    const match = String(fixedChargeDay).match(/^(\d{1,2})-(\d{1,2})$/);
    if (!match) return null;
    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `Annually on ${MONTH_LABELS_SHORT[month - 1]} ${day}`;
  }

  return null;
};

// Full natural-language form of a fixed recurring charge day, led by "Charges"
// and spelling out the day/month. Used when the fixed day is NOT crowded by a
// "Starts …" part (i.e. it's the leading part, alone or only with an "Ends …"),
// so the extra space the summary card would otherwise leave empty is filled
// with a clearer sentence: "Charges every Monday" /
// "Charges on the 1st of every month" (or "… on the last day of every month") /
// "Charges every January 3". Null when there's no fixed charge day.
const describeFixedChargeDayLong = (fixedChargeDay, selectedFrequency) => {
  if (fixedChargeDay === null || fixedChargeDay === undefined || fixedChargeDay === '') return null;

  if (selectedFrequency === 'WEEKLY') {
    const idx = parseInt(fixedChargeDay, 10);
    if (Number.isNaN(idx) || idx < 0 || idx > 6) return null;
    return `Charges every ${WEEKDAY_LABELS_LONG[idx]}`;
  }

  if (selectedFrequency === 'MONTHLY') {
    const day = parseInt(fixedChargeDay, 10);
    if (Number.isNaN(day) || day < 1 || day > 31) return null;
    return day === 31
      ? 'Charges on the last day of every month'
      : `Charges on the ${ordinal(day)} of every month`;
  }

  if (selectedFrequency === 'ANNUALLY') {
    const match = String(fixedChargeDay).match(/^(\d{1,2})-(\d{1,2})$/);
    if (!match) return null;
    const month = parseInt(match[1], 10);
    const day = parseInt(match[2], 10);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    return `Charges every ${MONTH_LABELS_LONG[month - 1]} ${day}`;
  }

  return null;
};

const ScheduledDonations = ({
  startDate,
  endDate,
  timeValue,
  // `brandColors` is still passed by callers; the row reads the brand roles as tokens now.
  selectedFrequency,
  defaultScheduledRange,
  scheduledDonationsOpen,
  setScheduledDonationsOpen,
  onStartDateChange,
  onEndDateChange,
  onTimeChange,
  onClear,
  onEdit,
  startDateError = '',
  endDateError = '',
  timeError = '',
  autoApplyVersion = 0,
  // Note: `formDisplayMode` is still passed by callers but no longer used here —
  // the summary now always wraps (never truncates) regardless of display mode.
}) => {
  const scheduleConfig = useMemo(
    () => getScheduleConfigForFrequency(defaultScheduledRange, selectedFrequency),
    [defaultScheduledRange, selectedFrequency],
  );
  const scheduleConfigKey = useMemo(
    () => createScheduleConfigKey(scheduleConfig, selectedFrequency),
    [scheduleConfig, selectedFrequency],
  );
  const autoApplyScopeKey = `${selectedFrequency || 'NONE'}:${scheduleConfigKey}:${autoApplyVersion}`;
  const autoAppliedScopeKeyRef = useRef(null);
  const suppressedScopeKeyRef = useRef(null);
  const previousFrequencyRef = useRef(selectedFrequency);
  const scheduleLinkRef = useRef(null);
  const focusLinkAfterClearRef = useRef(false);
  const lockedScheduleCallbacksRef = useRef({
    onStartDateChange,
    onEndDateChange,
    onTimeChange,
    setScheduledDonationsOpen,
  });

  const scheduleDefaults = useMemo(() => {
    if (!selectedFrequency || selectedFrequency === 'ONE_TIME') {
      return {
        startDate: null,
        endDate: null,
        timeValue: null,
        isLockedSchedule: false,
      };
    }

    return calculateScheduledDates(scheduleConfig, selectedFrequency);
  }, [scheduleConfig, selectedFrequency]);

  // Whenever the selected frequency changes, the previous frequency's
  // "already applied" guard is stale: the new frequency's org preset is
  // authoritative and must be eligible to (re)apply, overwriting any value
  // carried over from the prior frequency. Reset the guard on every frequency
  // change so the apply effect below isn't suppressed on a switch (incl. the
  // B→A→B round-trip, where the scope key returns to B's earlier value).
  //
  // Done in an effect (not a render-time ref mutation) so it's safe under
  // concurrent rendering — a discarded render must not clear the guard. This
  // effect is declared BEFORE the apply effect so, on a frequency change, the
  // guard is cleared first and the apply effect then sees it eligible to
  // re-apply in the same commit. Manual-clear suppression is also frequency
  // scoped: it survives every same-frequency rerender, but expires after the
  // donor leaves that frequency so returning can restore its authoritative preset.
  useEffect(() => {
    autoAppliedScopeKeyRef.current = null;
    suppressedScopeKeyRef.current = null;
  }, [selectedFrequency]);

  useEffect(() => {
    lockedScheduleCallbacksRef.current = {
      onStartDateChange,
      onEndDateChange,
      onTimeChange,
      setScheduledDonationsOpen,
    };
  }, [onEndDateChange, onStartDateChange, onTimeChange, setScheduledDonationsOpen]);

  // Apply the selected frequency's org preset. RULE (product decision):
  // the selected frequency's org preset is AUTHORITATIVE — selecting a frequency
  // always shows that frequency's configured schedule, overwriting any value
  // carried over from a previously-selected frequency (org preset OR donor edit).
  //
  // The guard keys on the SCOPE (frequency + config), not on whether a selection
  // already exists, so:
  //   - Switch A→B (B has a preset): scope changes → B's preset is (re)applied,
  //     replacing A's carried value. This fixes the carry-forward bug.
  //   - Donor edits B while staying on B: scope is unchanged and the guard is
  //     already set, so we do NOT clobber the donor's in-session edit on re-render.
  //   - Switch B→A→B: scope leaves and returns; the carry-over clear effect resets
  //     the guard when A clears, and the scope differs while on A, so returning to
  //     B re-applies B's preset (donor edits do NOT survive a frequency round-trip,
  //     per the chosen rule).
  useEffect(() => {
    const hasPresetSelection = hasScheduledSelection(scheduleDefaults);

    if (!selectedFrequency || selectedFrequency === 'ONE_TIME') return;
    if (scheduleDefaults.isLockedSchedule) return;
    if (!hasPresetSelection) return;
    if (suppressedScopeKeyRef.current === autoApplyScopeKey) return;
    if (autoAppliedScopeKeyRef.current === autoApplyScopeKey) return;

    // Already equals this frequency's preset → no state push needed.
    const alreadyMatchesThisPreset =
      formatDateValue(scheduleDefaults.startDate) === formatDateValue(startDate)
      && formatDateValue(scheduleDefaults.endDate) === formatDateValue(endDate)
      && (scheduleDefaults.timeValue || null) === (timeValue || null);
    if (alreadyMatchesThisPreset) {
      autoAppliedScopeKeyRef.current = autoApplyScopeKey;
      return;
    }

    onStartDateChange?.(scheduleDefaults.startDate || null);
    onEndDateChange?.(scheduleDefaults.endDate || null);
    onTimeChange?.(scheduleDefaults.timeValue || null);
    setScheduledDonationsOpen?.(true);
    autoAppliedScopeKeyRef.current = autoApplyScopeKey;
  }, [
    autoApplyScopeKey,
    endDate,
    onEndDateChange,
    onStartDateChange,
    onTimeChange,
    scheduleDefaults,
    selectedFrequency,
    setScheduledDonationsOpen,
    startDate,
    timeValue,
  ]);

  useEffect(() => {
    if (!scheduleDefaults.isLockedSchedule) return;

    const {
      onStartDateChange: applyStartDateChange,
      onEndDateChange: applyEndDateChange,
      onTimeChange: applyTimeChange,
      setScheduledDonationsOpen: applyScheduledDonationsOpen,
    } = lockedScheduleCallbacksRef.current;

    const nextStartDate = scheduleDefaults.startDate || null;
    const nextEndDate = scheduleDefaults.endDate || null;
    const nextTimeValue = scheduleDefaults.timeValue || null;

    const startChanged = Boolean(nextStartDate) !== Boolean(startDate)
      || formatDateValue(nextStartDate) !== formatDateValue(startDate);
    const endChanged = Boolean(nextEndDate) !== Boolean(endDate)
      || formatDateValue(nextEndDate) !== formatDateValue(endDate);
    const timeChanged = (timeValue || null) !== nextTimeValue;

    if (startChanged) {
      applyStartDateChange?.(nextStartDate);
    }
    if (endChanged) {
      applyEndDateChange?.(nextEndDate);
    }
    if (timeChanged) {
      applyTimeChange?.(nextTimeValue);
    }

    if (!scheduledDonationsOpen) {
      applyScheduledDonationsOpen?.(true);
    }
  }, [
    endDate,
    scheduleDefaults.endDate,
    scheduleDefaults.isLockedSchedule,
    scheduleDefaults.startDate,
    scheduleDefaults.timeValue,
    scheduledDonationsOpen,
    startDate,
    timeValue,
  ]);

  // Schedule state is shared by every frequency in the parent form. Owner call 2026-10-21
  // (QA #9): when the donor switches to a frequency with no configured schedule, their schedule
  // is KEPT and its end date re-fitted to the new frequency (the picker's snap rule), instead of
  // being silently cleared. Gate this on an actual frequency transition. FormFrequencySection
  // applies the same re-fit at the control; re-fitting an end that already fits changes nothing.
  useEffect(() => {
    const previousFrequency = previousFrequencyRef.current;
    previousFrequencyRef.current = selectedFrequency;

    if (!selectedFrequency || selectedFrequency === 'ONE_TIME') return;
    if (!previousFrequency || previousFrequency === selectedFrequency) return;
    if (scheduleDefaults.isLockedSchedule) return;
    if (hasScheduledSelection(scheduleDefaults)) return; // this frequency has a preset
    if (!hasScheduledSelection({ startDate, endDate, timeValue })) return; // nothing to keep

    const refitted = refitScheduledEndDate({ startDate, endDate, frequency: selectedFrequency });
    if (refitted) onEndDateChange?.(refitted);
    // Reset the auto-apply guard so returning to a frequency with an org preset re-applies it
    // (A → B → A shows A's preset again, per the preset-is-authoritative rule above).
    autoAppliedScopeKeyRef.current = null;
  }, [
    selectedFrequency,
    scheduleDefaults,
    startDate,
    endDate,
    timeValue,
    onEndDateChange,
  ]);

  const displayStartDate = scheduleDefaults.isLockedSchedule ? scheduleDefaults.startDate : startDate;
  const displayEndDate = scheduleDefaults.isLockedSchedule ? scheduleDefaults.endDate : endDate;
  const displayTimeValue = scheduleDefaults.isLockedSchedule ? scheduleDefaults.timeValue : timeValue;

  const showSavedSummary = scheduleDefaults.isLockedSchedule || scheduledDonationsOpen
    || hasScheduledSelection({
      startDate: displayStartDate,
      endDate: displayEndDate,
      timeValue: displayTimeValue,
    });
  const scheduleError = startDateError || endDateError || timeError;

  // After the × clears the schedule, the summary (and the focused ×) is replaced by the
  // "Schedule start / end date" link; focus moves there instead of dropping to the body.
  useEffect(() => {
    if (!showSavedSummary && focusLinkAfterClearRef.current) {
      focusLinkAfterClearRef.current = false;
      scheduleLinkRef.current?.focus({ preventScroll: true });
    }
  }, [showSavedSummary]);

  const clearScheduledDonation = () => {
    suppressedScopeKeyRef.current = autoApplyScopeKey;
    focusLinkAfterClearRef.current = true;

    if (onClear) {
      onClear();
      return;
    }

    onStartDateChange?.(null);
    onEndDateChange?.(null);
    onTimeChange?.(null);
    setScheduledDonationsOpen?.(false);
  };

  if (!showSavedSummary) {
    // The collapsed trigger is the same control in its other state, and it had
    // the same defect for the plainer reason: the brand's fill step painting
    // text. It sits on the form's own surface rather than on a brand-derived
    // ground, so the guard's default background is the right question here —
    // unlike the summary card below, which has to name its own.
    // CON-528: the trigger reads the brand TEXT role (derived from the org colour
    // and guarded to 4.5:1 on white), not the legacy HSB ramp step, with the
    // no-brand ink as its fallback.
    return (
      // 16 here plus the frequency section's own 4 below its segments makes 20 above the link,
      // the same as the form's section gap below it, so it sits evenly between the two.
      <div className="w-full mt-[var(--cs-space-4)]">
        <button
          ref={scheduleLinkRef}
          type="button"
          aria-haspopup="dialog"
          // Where the sheet returns focus when its opener has gone: a Save that clears the whole
          // schedule swaps the summary's Edit for this link.
          data-pane-sheet-return=""
          // CON-529 F11 (SPEC F2, Paper H0B-0): 14/20 medium in the brand text role. No underline
          // (owner call 2026-10-21): the plus icon already marks it as an action. Its hit area is
          // its own text, not the whole row.
          className="flex w-fit max-w-full items-center gap-1 bg-transparent py-0 text-left font-sans text-[length:var(--cs-text-sm)] font-medium leading-[var(--cs-leading-20)] no-underline focus-visible:[outline:var(--cs-focus-outline)] focus-visible:[outline-offset:var(--cs-focus-outline-offset)]"
          style={{
            color: 'var(--cs-brand-text, var(--cs-text-primary))',
          }}
          onClick={onEdit}
          onPointerEnter={prewarmPicker}
          onPointerDown={prewarmPicker}
          onFocus={prewarmPicker}
        >
          <PlusIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
          Schedule start / end date
        </button>
        <AnimatedError error={scheduleError} />
      </div>
    );
  }

  // Recurring-day anchor, used as the summary headline so a recurring schedule
  // reads naturally instead of a one-off "Starts MM/DD/YYYY". Shown whenever the
  // org configured a fixed charge day for this frequency — locked or not.
  // Two forms (chosen below based on how crowded the line is):
  //   compact — "Every Wed" / "Monthly on the 31st" / "Annually on Jan 3"
  //   long    — "Charges every Wednesday" / "Charges on the 31st of every month"
  //             / "Charges every January 3"
  const fixedChargeDay = scheduleConfig?.fixedChargeDay;
  const fixedChargeDayShort = describeFixedChargeDayShort(fixedChargeDay, selectedFrequency);
  const fixedChargeDayLong = describeFixedChargeDayLong(fixedChargeDay, selectedFrequency);

  // Decide whether to show "Starts …".
  //
  // No fixed charge day → show it whenever the start date is in the FUTURE. A
  // start of today (or earlier) means charging effectively begins now, so the
  // date is noise.
  //
  // Fixed charge day → the first charge lands on the first anchor occurrence
  // (e.g. next Mon / next 31st) regardless of an earlier start, so the start
  // date only matters when it pushes the first charge PAST that first anchor
  // occurrence. Compare start to `firstAnchor` (next anchor date from today),
  // not to today: show "Starts …" only when start > firstAnchor.
  let showStart = false;
  if (displayStartDate) {
    const start = dayjs(displayStartDate).startOf('day');
    if (fixedChargeDay !== null && fixedChargeDay !== undefined && fixedChargeDay !== '') {
      const firstAnchorStr = resolveFixedChargeDayStartDate(
        fixedChargeDay,
        selectedFrequency,
        displayTimeValue,
      );
      // If we can't resolve an anchor (shouldn't happen for a valid config),
      // fall back to the future-of-today rule so we never hide a real start.
      const firstAnchor = firstAnchorStr ? dayjs(firstAnchorStr).startOf('day') : null;
      showStart = firstAnchor
        ? start.isAfter(firstAnchor, 'day')
        : start.isAfter(dayjs().startOf('day'), 'day');
    } else {
      showStart = start.isAfter(dayjs().startOf('day'), 'day');
    }
  }

  // Assemble all parts chronologically on one line (same font and color):
  //   Starts <future first charge date> · <anchor cadence> · Ends <end date>
  // Each part only when meaningful.
  //
  // Fixed-day phrasing: when there is NO "Starts …" part the fixed day leads the
  // line (alone, or only with an "Ends …"), so there's room to spell it out fully
  // ("Charges every Wednesday"). When "Starts …" is present too the line is
  // crowded with three parts, so fall back to the compact form ("Every Wed") to
  // keep it readable.
  //
  // The chosen time is part of the sentence ("Starts Oct 25, 2026 at 3:00 PM"), never only a
  // tooltip a touch user cannot hover. "now" adds nothing. A specific time with no future start
  // to hang it on reads "Starts today at 3:00 PM" (the first charge is today).
  //
  // Two lines at most (QA round 2, F5): line 1 is when it starts (the start with its time, or the
  // fixed day), line 2 the rest (the compact fixed day when a start leads, then "Ends …"). In a
  // narrow row (below the @xs container size, i.e. a 320 phone) the compact variant drops the
  // year of a date in the current year, uses the compact fixed-day form and drops the time
  // (owner review 2026-10-28), so the start stays on one line; Edit still shows the time.
  const formattedTime = displayTimeValue && displayTimeValue !== 'now' ? formatTimeValue(displayTimeValue) : null;
  // Non-breaking spaces keep "at 11:00 AM" in one piece: a narrow card wraps before
  // "at", never between the time and its AM/PM.
  const atTime = formattedTime ? ` at\u00A0${formattedTime.replace(/ /g, '\u00A0')}` : '';
  const buildLines = (compact) => {
    const at = compact ? '' : atTime;
    const formatDate = (value) => {
      if (!compact) return formatDateValueLong(value);
      const day = dayjs(value);
      return day.isSame(dayjs(), 'year') ? day.format('MMM D') : day.format('MMM D, YYYY');
    };
    const fixedLead = compact ? fixedChargeDayShort : (fixedChargeDayLong || fixedChargeDayShort);
    const first = [];
    const second = [];
    if (showStart) {
      first.push(`Starts ${formatDate(displayStartDate)}${at}`);
      if (fixedChargeDayShort) second.push(fixedChargeDayShort);
    } else if (fixedLead) {
      first.push(`${fixedLead}${at}`);
    } else if (formattedTime) {
      first.push(`Starts today${at}`);
    }
    if (displayEndDate) (first.length ? second : first).push(`Ends ${formatDate(displayEndDate)}`);
    const lines = [first.join(' · '), second.join(' · ')].filter(Boolean);
    return lines.length ? lines : ['Scheduled donation saved'];
  };
  const fullLines = buildLines(false);
  const compactLines = buildLines(true);
  // One plain sentence for tests and the accessible text of the row.
  const scheduleSummary = fullLines.join(' · ');

  // A token row on the form's own surface, the same edge and corner as the fields around it:
  // clock · the schedule sentence · Edit · ×. Edit reads the brand text role (guarded to 4.5:1
  // on that white surface); × is the strip icon button with the shared X glyph.
  return (
    <AnimatedAccordionReveal contentKey="scheduled-donations-summary">
      {/* The container the row's narrow layout queries (below @xs, a 320 phone). */}
      <div className="@container w-full mt-[var(--cs-space-3)]">
        <div
          data-testid="scheduled-donations-summary"
          // QA round 2, F5: at most two text lines at every width. Edit and × hold the top-right
          // corner beside line 1; line 2 (the rest) runs the full width below them. A one-line
          // summary sits beside them.
          className={`grid min-h-[var(--cs-input-height)] grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-[var(--cs-space-2)] rounded-[var(--cs-input-radius)] border py-[var(--cs-space-1)] pl-[var(--cs-input-padding-x)] pr-[var(--cs-space-1)] ${
            scheduleError
              ? 'border-[color:var(--cs-error-color)] bg-[color:var(--cs-status-error-bg)]'
              : 'border-[color:var(--cs-border-control)] bg-[color:var(--cs-surface-0)]'
          }`}
        >
          <div
            data-schedule-summary-text={scheduleSummary}
            className="contents break-words font-sans text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] font-medium text-[color:var(--cs-text-primary)]"
          >
            {/* Each line carries its wide and its compact wording; only one is displayed, so a
                screen reader reads one. */}
            {fullLines.map((line, index) => (
              <span
                key={line}
                data-summary-line={index}
                className={`block min-w-0 ${index > 0 ? 'col-span-3' : ''} ${index === 0 && fullLines.length > 1 ? 'pt-[var(--cs-space-1)]' : ''} ${index === fullLines.length - 1 ? 'pb-[var(--cs-space-1)]' : ''}`}
              >
                <span className="@max-xs:hidden">{line}</span>
                <span className="hidden @max-xs:inline">{compactLines[index] ?? line}</span>
              </span>
            ))}
          </div>
          {!scheduleDefaults.isLockedSchedule && (
            <>
              <button
                type="button"
                // `bg-transparent` is load-bearing: this app reverts the button background to the
                // UA default (an opaque grey) for Tailwind v3 parity.
                // Pinned to the first row so Edit and × sit in the top-right corner.
                className="col-start-2 row-start-1 shrink-0 rounded-[var(--cs-control-radius)] border-0 bg-transparent px-[var(--cs-space-1)] py-0 font-sans text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] font-medium text-[color:var(--cs-brand-text,var(--cs-text-primary))] focus-visible:[outline:var(--cs-focus-outline)] focus-visible:[outline-offset:var(--cs-focus-outline-offset)]"
                aria-label="Edit schedule"
                // Focus lands here when the schedule sheet closes after a save (PaneSheet).
                data-pane-sheet-return=""
                aria-haspopup="dialog"
                onClick={onEdit}
                onPointerEnter={prewarmPicker}
                onPointerDown={prewarmPicker}
                onFocus={prewarmPicker}
              >
                Edit
              </button>
              <button
                type="button"
                className={`col-start-3 row-start-1 ${STRIP_ICON_BUTTON_CLASS}`}
                aria-label="Clear scheduled donation"
                onClick={clearScheduledDonation}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className={`${STRIP_ICON_CLASS} [stroke-width:var(--cs-icon-stroke)]`} aria-hidden="true">
                  <path d="M18 6 6 18" />
                  <path d="m6 6 12 12" />
                </svg>
              </button>
            </>
          )}
          {scheduleError && (
            <div className="col-span-full pb-[var(--cs-space-1)]">
              <AnimatedError error={scheduleError} />
            </div>
          )}
        </div>
      </div>
    </AnimatedAccordionReveal>
  );
};

export default ScheduledDonations;
